import React, { useState, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Upload, CheckCircle, AlertTriangle, X } from 'lucide-react';
import { filesApi, suppliersApi, tasksApi } from '../services/api';
import { useAuth } from '../context/AuthContext';

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const UploadPage = () => {
  const queryClient = useQueryClient();
  const [selectedFiles, setSelectedFiles] = useState({});
  const [uploadingSupplier, setUploadingSupplier] = useState(null); // { id, name }
  const [uploadResult, setUploadResult] = useState(null); // result modal data
  const pollTimerRef = useRef(null);

  // Получаем информацию о текущем пользователе из AuthContext
  const { user, isAdmin } = useAuth();

  // Загружаем список поставщиков с self_upload=true
  const { data: suppliers, isLoading } = useQuery({
    queryKey: ['suppliers-self-upload'],
    queryFn: async () => {
      const res = await suppliersApi.getAll();
      // Фильтруем только self_upload XLS и PDF
      let selfUploadSuppliers = res.data.filter(s => s.self_upload && ['xls', 'pdf'].includes(s.update_method));
      
      // Если это не админ, показываем только поставщиков пользователя
      if (!isAdmin && user?.supplier_ids && user.supplier_ids.length > 0) {
        selfUploadSuppliers = selfUploadSuppliers.filter(s => user.supplier_ids.includes(s.id));
      }
      
      // Для каждого поставщика получаем конфигурацию
      const suppliersWithConfig = await Promise.all(
        selfUploadSuppliers.map(async (supplier) => {
          try {
            const configRes = await suppliersApi.getConfigPublic(supplier.id);
            return { ...supplier, config: configRes.data };
          } catch (error) {
            return { ...supplier, config: null };
          }
        })
      );
      
      return suppliersWithConfig;
    },
  });

  const pollTaskStatus = (taskId, supplierName) => {
    let attempts = 0;
    const maxAttempts = 180; // 3 минуты

    const pollProcessing = async (processingTaskId) => {
      attempts++;
      try {
        const res = await tasksApi.getById(processingTaskId);
        const status = res.data.status;

        if (status === 'SUCCESS') {
          const result = res.data.result || {};
          setUploadingSupplier(null);
          setUploadResult(prev => ({
            ...prev,
            updated: (result.stocks_updated || 0) + (result.stocks_created || 0),
            totalProcessed: result.total_processed || 0,
            notFound: result.not_found || 0,
            loading: false,
          }));
          queryClient.invalidateQueries(['suppliers-self-upload']);
          return;
        }

        if (status === 'FAILURE') {
          setUploadingSupplier(null);
          setUploadResult(prev => ({
            ...prev,
            success: false,
            error: res.data.error || 'Ошибка обработки данных',
            loading: false,
          }));
          queryClient.invalidateQueries(['suppliers-self-upload']);
          return;
        }

        if (attempts < maxAttempts) {
          pollTimerRef.current = setTimeout(() => pollProcessing(processingTaskId), 1000);
        } else {
          setUploadingSupplier(null);
          setUploadResult(prev => ({ ...prev, loading: false, timedOut: true }));
        }
      } catch (e) {
        if (attempts < maxAttempts) {
          pollTimerRef.current = setTimeout(() => pollProcessing(processingTaskId), 2000);
        }
      }
    };

    const poll = async () => {
      attempts++;
      try {
        const res = await tasksApi.getById(taskId);
        const status = res.data.status;

        if (status === 'SUCCESS') {
          const result = res.data.result || {};
          // parse_supplier_file запускает process_products_data — нужно ждать его
          if (result.processing_task_id) {
            attempts = 0;
            setUploadResult(prev => ({ ...prev, stage: 'processing' }));
            pollTimerRef.current = setTimeout(() => pollProcessing(result.processing_task_id), 1000);
          } else {
            // Нет вложенной задачи — используем что есть
            setUploadingSupplier(null);
            setUploadResult(prev => ({
              ...prev,
              updated: (result.stocks_updated || 0) + (result.stocks_created || 0),
              totalProcessed: result.total_processed || 0,
              notFound: result.not_found || 0,
              loading: false,
            }));
            queryClient.invalidateQueries(['suppliers-self-upload']);
          }
          return;
        }

        if (status === 'FAILURE') {
          setUploadingSupplier(null);
          setUploadResult(prev => ({
            ...prev,
            success: false,
            error: res.data.error || 'Ошибка обработки файла',
            loading: false,
          }));
          queryClient.invalidateQueries(['suppliers-self-upload']);
          return;
        }

        if (attempts < maxAttempts) {
          pollTimerRef.current = setTimeout(poll, 1000);
        } else {
          setUploadingSupplier(null);
          setUploadResult(prev => ({ ...prev, loading: false, timedOut: true }));
        }
      } catch (e) {
        if (attempts < maxAttempts) {
          pollTimerRef.current = setTimeout(poll, 2000);
        }
      }
    };

    pollTimerRef.current = setTimeout(poll, 1500);
  };

  const uploadMutation = useMutation({
    mutationFn: ({ formData, supplierId, method }) =>
      method === 'pdf' ? filesApi.uploadPdfSelf(formData) : filesApi.uploadXlsSelf(formData),
    onSuccess: (res, variables) => {
      const taskId = res.data.task_id;
      const supplier = suppliers?.find(s => s.id === variables.supplierId);
      const supplierName = supplier?.name || 'Поставщик';

      // Очищаем выбранный файл для этого поставщика
      setSelectedFiles(prev => {
        const newFiles = { ...prev };
        delete newFiles[variables.supplierId];
        return newFiles;
      });

      if (taskId) {
        setUploadingSupplier({ id: variables.supplierId, name: supplierName });
        // Сразу открываем модалку в состоянии загрузки
        setUploadResult({
          supplierName,
          success: true,
          loading: true,
          stage: 'uploading',
          updated: 0,
          totalProcessed: 0,
          notFound: 0,
        });
        pollTaskStatus(taskId, supplierName);
      } else {
        toast.success(res.data.message || 'Файл загружен и отправлен в обработку');
        queryClient.invalidateQueries(['suppliers-self-upload']);
      }
    },
    onError: (err) => toast.error(err?.response?.data?.detail || 'Ошибка загрузки'),
  });

  const handleFileSelect = (supplierId, file) => {
    setSelectedFiles(prev => ({
      ...prev,
      [supplierId]: file
    }));
  };

  const handleUpload = async (supplierId, method) => {
    const file = selectedFiles[supplierId];
    if (!file) {
      toast.error('Выберите файл');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('supplier_id', supplierId);

    uploadMutation.mutate({ formData, supplierId, method });
  };

  const closeResultModal = () => {
    setUploadResult(null);
  };

  if (isLoading) {
    return (
      <div>
        <h1 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '20px' }}>
          Загрузка файлов поставщиков
        </h1>
        <div>Загрузка...</div>
      </div>
    );
  }

  if (!suppliers || suppliers.length === 0) {
    return (
      <div>
        <h1 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '20px' }}>
          Загрузка файлов поставщиков
        </h1>
        <div className="card">
          <p style={{ color: '#6b7280' }}>
            {!isAdmin && user?.supplier_ids && user.supplier_ids.length > 0 
              ? `Нет доступных поставщиков для загрузки файлов. Ваши поставщики: ${user.supplier_names?.join(', ')}`
              : 'Нет поставщиков с включенной ручной загрузкой файлов.'
            }
          </p>
          <p style={{ color: '#6b7280', marginTop: '8px' }}>
            Включите галку "Загружаем сами" в настройках поставщика с типом XLS.
          </p>
        </div>
      </div>
    );
  }

  const isUploading = uploadMutation.isPending || !!uploadingSupplier;

  return (
    <div>
      <h1 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '20px' }}>
        Загрузка файлов поставщиков
      </h1>

      <div className="card" style={{ padding: '0', overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ 
              background: '#f9fafb', 
              borderBottom: '2px solid #e5e7eb',
              textAlign: 'left'
            }}>
              <th style={{ padding: '12px 16px', fontWeight: '600', fontSize: '14px' }}>Поставщик</th>
              <th style={{ padding: '12px 16px', fontWeight: '600', fontSize: '14px' }}>Артикул (колонка)</th>
              <th style={{ padding: '12px 16px', fontWeight: '600', fontSize: '14px' }}>Бренд (колонка)</th>
              <th style={{ padding: '12px 16px', fontWeight: '600', fontSize: '14px' }}>Остаток (колонка)</th>
              <th style={{ padding: '12px 16px', fontWeight: '600', fontSize: '14px' }}>Дата обновления</th>
              <th style={{ padding: '12px 16px', fontWeight: '600', fontSize: '14px' }}>В наличии</th>
              <th style={{ padding: '12px 16px', fontWeight: '600', fontSize: '14px' }}>Файл</th>
              <th style={{ padding: '12px 16px', fontWeight: '600', fontSize: '14px' }}>Действие</th>
            </tr>
          </thead>
          <tbody>
            {suppliers.map((supplier) => {
              const isThisUploading = uploadingSupplier?.id === supplier.id;
              
              // Проверяем просрочена ли загрузка
              const isOverdue = supplier.last_stock_sync && supplier.update_interval_days && 
                Math.floor((Date.now() - new Date(supplier.last_stock_sync)) / 86400000) > supplier.update_interval_days;
              
              return (
                <tr 
                  key={supplier.id} 
                  style={{ 
                    borderBottom: '1px solid #e5e7eb',
                    background: isOverdue ? '#fef2f2' : 'transparent'
                  }}
                >
                  <td style={{ padding: '12px 16px', fontWeight: '500', color: isOverdue ? '#991b1b' : '#111827' }}>
                    {supplier.name}
                    {isThisUploading && (
                      <span style={{ marginLeft: '8px', fontSize: '12px', color: '#6b7280' }}>
                        ⏳ обработка...
                      </span>
                    )}
                    {isOverdue && !isThisUploading && (
                      <span style={{ marginLeft: '8px', fontSize: '12px', color: '#dc2626', fontWeight: '600' }}>
                        ⚠️ Просрочено
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: '14px', color: isOverdue ? '#991b1b' : '#6b7280' }}>
                    {supplier.config?.column_sku || 'B'}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: '14px', color: isOverdue ? '#991b1b' : '#6b7280' }}>
                    {supplier.config?.column_brand || 'B'}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: '14px', color: isOverdue ? '#991b1b' : '#6b7280' }}>
                    {supplier.config?.column_stock || 'C'}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: '13px', color: isOverdue ? '#dc2626' : (supplier.last_stock_sync ? '#374151' : '#9ca3af'), fontWeight: isOverdue ? '600' : '400' }}>
                    {formatDate(supplier.last_stock_sync)}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: '14px', fontWeight: '500', color: (supplier.in_stock_products || 0) > 0 ? '#059669' : '#9ca3af' }}>
                    {supplier.in_stock_products || 0}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <label
                        htmlFor={`file-${supplier.id}`}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '6px 12px',
                          fontSize: '13px',
                          fontWeight: '500',
                          color: selectedFiles[supplier.id] ? '#059669' : '#374151',
                          background: selectedFiles[supplier.id] ? '#d1fae5' : '#f9fafb',
                          border: '1px solid',
                          borderColor: selectedFiles[supplier.id] ? '#059669' : '#d1d5db',
                          borderRadius: '6px',
                          cursor: isUploading ? 'not-allowed' : 'pointer',
                          transition: 'all 0.2s',
                          maxWidth: '240px',
                          opacity: isUploading ? 0.6 : 1,
                        }}
                      >
                        <span style={{ fontSize: '16px' }}>
                          {selectedFiles[supplier.id] ? '✓' : '📁'}
                        </span>
                        <span style={{ 
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {selectedFiles[supplier.id] 
                            ? selectedFiles[supplier.id].name 
                            : 'Выберите файл'
                          }
                        </span>
                      </label>
                      <input
                        id={`file-${supplier.id}`}
                        type="file"
                        accept={supplier.update_method === 'pdf' ? '.pdf' : '.xls,.xlsx'}
                        onChange={(e) => handleFileSelect(supplier.id, e.target.files[0])}
                        style={{ display: 'none' }}
                        disabled={isUploading}
                      />
                    </div>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <button
                      className="btn btn-primary"
                      onClick={() => handleUpload(supplier.id, supplier.update_method)}
                      disabled={!selectedFiles[supplier.id] || isUploading}
                      style={{
                        padding: '6px 16px',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <Upload size={16} />
                      {isThisUploading ? 'Обработка...' : isUploading ? 'Подождите...' : 'Загрузить'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Модальное окно результата загрузки */}
      {uploadResult && (
        <div
          className="modal-overlay"
          onClick={closeResultModal}
          style={{ zIndex: 1100 }}
        >
          <div
            className="modal-content"
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: '460px' }}
          >
            <div className="modal-header">
              <h2 style={{ fontSize: '16px', fontWeight: '600' }}>
                Результат загрузки — {uploadResult.supplierName}
              </h2>
              <button className="modal-close" onClick={closeResultModal}>×</button>
            </div>
            <div className="modal-body" style={{ textAlign: 'center', padding: '24px 16px' }}>
              {uploadResult.loading ? (
                <div>
                  <div style={{ fontSize: '40px', marginBottom: '12px' }}>
                    {uploadResult.stage === 'uploading' ? '📤' : '⚙️'}
                  </div>
                  <div style={{ fontSize: '16px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                    {uploadResult.stage === 'uploading' ? 'Загрузка файла...' : 'Обработка данных...'}
                  </div>
                  <div style={{ fontSize: '13px', color: '#6b7280' }}>
                    Это может занять несколько минут
                  </div>
                  <div style={{ marginTop: '16px', height: '4px', background: '#e5e7eb', borderRadius: '2px', overflow: 'hidden' }}>
                    <div style={{
                      height: '100%',
                      background: 'linear-gradient(90deg, #3b82f6 0%, #93c5fd 50%, #3b82f6 100%)',
                      backgroundSize: '200% 100%',
                      borderRadius: '2px',
                      width: '100%',
                    }} />
                  </div>
                </div>
              ) : uploadResult.timedOut ? (
                <>
                  <div style={{ fontSize: '40px', marginBottom: '12px' }}>ℹ️</div>
                  <div style={{ fontSize: '16px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                    Обработка продолжается
                  </div>
                  <div style={{ fontSize: '13px', color: '#6b7280' }}>
                    Файл отправлен в обработку. Проверьте раздел "Задачи" для просмотра результата.
                  </div>
                </>
              ) : uploadResult.success ? (
                <>
                  {uploadResult.updated > 0 ? (
                    <>
                      <CheckCircle size={48} color="#16a34a" style={{ marginBottom: '12px' }} />
                      <div style={{ fontSize: '22px', fontWeight: '700', color: '#16a34a', marginBottom: '8px' }}>
                        Успешно обновлено {uploadResult.updated} товаров
                      </div>
                      {uploadResult.notFound > 0 && (
                        <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '8px' }}>
                          Не найдено в базе: {uploadResult.notFound} позиций
                        </div>
                      )}
                      {uploadResult.totalProcessed > 0 && (
                        <div style={{ fontSize: '13px', color: '#9ca3af', marginTop: '4px' }}>
                          Обработано строк в файле: {uploadResult.totalProcessed}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <AlertTriangle size={48} color="#d97706" style={{ marginBottom: '12px' }} />
                      <div style={{ fontSize: '18px', fontWeight: '700', color: '#d97706', marginBottom: '8px' }}>
                        Обновлено 0 товаров
                      </div>
                      <div style={{ fontSize: '14px', color: '#92400e', background: '#fef3c7', borderRadius: '8px', padding: '12px', marginTop: '12px', lineHeight: '1.5' }}>
                        ⚠️ Проверь правильность колонок в файле и выполни загрузку ещё раз
                      </div>
                      {uploadResult.notFound > 0 && (
                        <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '12px' }}>
                          Не найдено в базе: {uploadResult.notFound} позиций
                        </div>
                      )}
                    </>
                  )}
                </>
              ) : (
                <>
                  <X size={48} color="#dc2626" style={{ marginBottom: '12px' }} />
                  <div style={{ fontSize: '18px', fontWeight: '700', color: '#dc2626', marginBottom: '8px' }}>
                    Ошибка обработки
                  </div>
                  <div style={{ fontSize: '13px', color: '#6b7280' }}>{uploadResult.error}</div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeResultModal}>Закрыть</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadPage;
